import { BalancePill, dayLabel } from "./utils";

/**
 * Presentational panel for the "Pending" tab of Team Approvals.
 * All state and callbacks are lifted to <TeamApprovals />; this file owns
 * only rendering (desktop table + mobile card list).
 */
export default function ApprovalsPending({
  items,
  employees: _employees,       // kept for signature parity if callers need it
  selected,
  toggleSel,
  toggleSelAll,
  noteFor,
  setNoteFor,
  coverFor,
  setCoverFor,
  coverOptionsByLeave,
  confirmFor,
  setConfirmFor,
  busyId,
  decide,
}) {
  if (items.length === 0) {
    return (
      <div className="px-5 py-10 text-center text-xs font-mono text-zinc-400" data-testid="team-approvals-empty">
        NO PENDING APPROVALS
      </div>
    );
  }

  return (
    <>
      {/* Mobile card view — one card per pending leave */}
      <div className="md:hidden divide-y divide-zinc-200" data-testid="team-approvals-mobile-list">
        {items.map((l) => (
          <MobileCard
            key={l.id}
            l={l}
            selected={selected}
            toggleSel={toggleSel}
            coverFor={coverFor}
            setCoverFor={setCoverFor}
            coverOptionsByLeave={coverOptionsByLeave}
            noteFor={noteFor}
            setNoteFor={setNoteFor}
            busyId={busyId}
            decide={decide}
          />
        ))}
      </div>

      {/* Desktop table view */}
      <table className="w-full text-sm hidden md:table">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="w-10 text-center px-2 py-2.5">
              <input
                type="checkbox"
                checked={selected.size === items.length && items.length > 0}
                onChange={toggleSelAll}
                className="accent-[#FF3E00]"
                data-testid="bulk-select-all"
                aria-label="Select all"
              />
            </th>
            <th className="text-left px-4 py-2.5">Employee</th>
            <th className="text-left px-4 py-2.5">Dept</th>
            <th className="text-left px-4 py-2.5">Type</th>
            <th className="text-left px-4 py-2.5">Dates</th>
            <th className="text-left px-4 py-2.5">Days</th>
            <th className="text-left px-4 py-2.5">Reason / Note</th>
            <th className="w-56" />
          </tr>
        </thead>
        <tbody data-testid="team-approvals-tbody">
          {items.map((l) => (
            <DesktopRow
              key={l.id}
              l={l}
              selected={selected}
              toggleSel={toggleSel}
              coverFor={coverFor}
              setCoverFor={setCoverFor}
              coverOptionsByLeave={coverOptionsByLeave}
              noteFor={noteFor}
              setNoteFor={setNoteFor}
              confirmFor={confirmFor}
              setConfirmFor={setConfirmFor}
              busyId={busyId}
              decide={decide}
            />
          ))}
        </tbody>
      </table>
    </>
  );
}

function MobileCard({
  l, selected, toggleSel, coverFor, setCoverFor, coverOptionsByLeave,
  noteFor, setNoteFor, busyId, decide,
}) {
  const hasConflicts = (l.conflicts || []).length > 0;
  return (
    <div className={`p-4 ${hasConflicts ? "bg-amber-50/40" : ""}`} data-testid={`mobile-card-${l.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold truncate">{l.employee_name || l.employee_id}</div>
          <div className="text-[10px] font-mono text-zinc-400 truncate">{l.employee_no} · {l.employee_department || l.department}</div>
        </div>
        <input
          type="checkbox"
          checked={selected.has(l.id)}
          onChange={() => toggleSel(l.id)}
          className="accent-[#FF3E00] mt-0.5"
          data-testid={`bulk-select-mobile-${l.id}`}
          aria-label={`Select ${l.employee_name}`}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        <BalancePill balance={l.balance} type="annual" />
        <BalancePill balance={l.balance} type="sick" />
        {l.half_day && <span className="inline-block text-[10px] font-mono border px-1.5 py-0.5 mr-1 bg-white text-[#FF3E00] border-[#FF3E00]">{l.half_day.toUpperCase()} ½</span>}
      </div>
      <div className="mt-2 text-sm">
        <span className="capitalize">{l.type}</span>
        <span className="mx-1.5 text-zinc-400">·</span>
        <span className="tnum">{l.start_date} → {l.end_date}</span>
        <span className="mx-1.5 text-zinc-400">·</span>
        <span className="font-semibold tnum">{dayLabel(l)}d</span>
        {typeof l.days_calendar === "number" && l.days_calendar !== l.days_requested && (
          <span className="text-[10px] font-mono text-zinc-400"> ({l.days_calendar}d span)</span>
        )}
      </div>
      {l.reason && <div className="mt-1 text-xs text-zinc-600">{l.reason}</div>}
      {hasConflicts && (
        <div className="mt-2 border border-amber-400 bg-amber-50 text-amber-800 text-[10px] font-mono px-2 py-1" data-testid={`conflicts-mobile-${l.id}`}>
          ⚠ {l.conflicts.length} DEPT CONFLICT{l.conflicts.length > 1 ? "S" : ""} · {l.conflicts.slice(0, 2).map((c) => c.employee_name).join(", ")}
        </div>
      )}
      <div className="mt-2">
        <label className="text-[9px] font-mono uppercase text-zinc-500 block mb-0.5">Cover</label>
        <select
          value={coverFor[l.id] || ""}
          onChange={(e) => setCoverFor((c) => ({ ...c, [l.id]: e.target.value }))}
          className="w-full text-xs border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 outline-none"
          data-testid={`cover-select-mobile-${l.id}`}
        >
          <option value="">— no cover —</option>
          {(coverOptionsByLeave[l.id] || []).map((opt) => (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </select>
      </div>
      <input
        value={noteFor[l.id] || ""}
        onChange={(e) => setNoteFor((n) => ({ ...n, [l.id]: e.target.value }))}
        placeholder="Optional decision note"
        className="w-full mt-2 border-b border-zinc-200 focus:border-zinc-950 bg-transparent py-1 text-xs outline-none"
        data-testid={`approval-note-mobile-${l.id}`}
      />
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          disabled={busyId === l.id}
          onClick={() => decide(l, "approve")}
          className="text-xs font-mono uppercase text-white bg-emerald-700 py-2.5 disabled:opacity-40"
          data-testid={`approval-approve-mobile-${l.id}`}
        >
          Approve
        </button>
        <button
          disabled={busyId === l.id}
          onClick={() => decide(l, "reject")}
          className="text-xs font-mono uppercase text-white bg-[#D0021B] py-2.5 disabled:opacity-40"
          data-testid={`approval-reject-mobile-${l.id}`}
        >
          Reject
        </button>
      </div>
    </div>
  );
}

function DesktopRow({
  l, selected, toggleSel, coverFor, setCoverFor, coverOptionsByLeave,
  noteFor, setNoteFor, confirmFor, setConfirmFor, busyId, decide,
}) {
  const isConfirming = confirmFor && confirmFor.id === l.id;
  const hasConflicts = (l.conflicts || []).length > 0;
  return (
    <tr className={`border-b border-zinc-100 align-top ${hasConflicts ? "bg-amber-50/40" : ""}`}>
      <td className="text-center px-2 py-3">
        <input
          type="checkbox"
          checked={selected.has(l.id)}
          onChange={() => toggleSel(l.id)}
          className="accent-[#FF3E00]"
          data-testid={`bulk-select-${l.id}`}
          aria-label={`Select ${l.employee_name || l.id}`}
        />
      </td>
      <td className="px-4 py-3 font-medium">
        {l.employee_name || l.employee_id}
        {l.employee_no && <div className="text-[10px] font-mono text-zinc-400">{l.employee_no}</div>}
        <div className="mt-1.5">
          <BalancePill balance={l.balance} type="annual" />
          <BalancePill balance={l.balance} type="sick" />
        </div>
      </td>
      <td className="px-4 py-3 text-zinc-600">{l.employee_department || l.department || "—"}</td>
      <td className="px-4 py-3 capitalize text-zinc-600">
        {l.type}
        {l.half_day && (
          <span className="ml-1 text-[9px] font-mono uppercase text-[#FF3E00] border border-[#FF3E00] px-1" data-testid={`halfday-${l.id}`}>
            {l.half_day}
          </span>
        )}
      </td>
      <td className="px-4 py-3 tnum text-xs">
        <div>{l.start_date}</div>
        <div className="text-zinc-400">→ {l.end_date}</div>
      </td>
      <td className="px-4 py-3 tnum font-semibold" data-testid={`days-${l.id}`}>
        {dayLabel(l)}
        {typeof l.days_calendar === "number" && l.days_calendar !== l.days_requested && (
          <div className="text-[9px] font-mono text-zinc-400">of {l.days_calendar}d span</div>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-zinc-600 max-w-xs">
        <div className="mb-1">{l.reason || <span className="text-zinc-400">— no reason —</span>}</div>
        {hasConflicts && (
          <div
            className="mt-1 border border-amber-400 bg-amber-50 text-amber-800 text-[10px] font-mono px-2 py-1"
            title={l.conflicts.map((c) => `${c.employee_name} (${c.start_date}→${c.end_date}, ${c.status})`).join("\n")}
            data-testid={`conflicts-${l.id}`}
          >
            ⚠ {l.conflicts.length} DEPT CONFLICT{l.conflicts.length > 1 ? "S" : ""} · {l.conflicts.slice(0, 2).map((c) => c.employee_name).join(", ")}
            {l.conflicts.length > 2 ? "…" : ""}
          </div>
        )}
        <div className="mt-1.5 flex items-center gap-1">
          <span className="text-[9px] font-mono uppercase text-zinc-500">Cover:</span>
          <select
            value={coverFor[l.id] || ""}
            onChange={(e) => setCoverFor((c) => ({ ...c, [l.id]: e.target.value }))}
            data-testid={`cover-select-${l.id}`}
            className="flex-1 text-[11px] border-b border-zinc-200 focus:border-zinc-950 bg-transparent py-0.5 outline-none"
            title="Choose a colleague to cover for this leave"
          >
            <option value="">— no cover —</option>
            {(coverOptionsByLeave[l.id] || []).map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>
        <input
          value={noteFor[l.id] || ""}
          onChange={(e) => setNoteFor((n) => ({ ...n, [l.id]: e.target.value }))}
          placeholder="Optional decision note"
          data-testid={`approval-note-${l.id}`}
          className="w-full mt-1 border-b border-zinc-200 focus:border-zinc-950 bg-transparent py-1 text-xs outline-none"
        />
      </td>
      <td className="px-4 py-3">
        {isConfirming ? (
          <div className="flex flex-col gap-1" data-testid={`confirm-block-${l.id}`}>
            <div className="text-[9px] font-mono text-amber-800 uppercase">Approve anyway?</div>
            <div className="flex gap-2">
              <button
                disabled={busyId === l.id}
                onClick={() => decide(l, "approve", { skipConfirm: true })}
                className="text-[10px] font-mono text-white bg-[#D0021B] border border-[#D0021B] px-2 py-1 hover:bg-red-800"
                data-testid={`approval-approve-confirm-${l.id}`}
              >
                YES, APPROVE
              </button>
              <button onClick={() => setConfirmFor(null)} className="text-[10px] font-mono text-zinc-600 hover:text-zinc-950">CANCEL</button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              disabled={busyId === l.id}
              onClick={() => decide(l, "approve")}
              className="text-[10px] font-mono text-emerald-700 border border-emerald-700 px-2 py-1 hover:bg-emerald-700 hover:text-white disabled:opacity-40"
              data-testid={`approval-approve-${l.id}`}
            >
              APPROVE
            </button>
            <button
              disabled={busyId === l.id}
              onClick={() => decide(l, "reject")}
              className="text-[10px] font-mono text-[#D0021B] border border-[#D0021B] px-2 py-1 hover:bg-[#D0021B] hover:text-white disabled:opacity-40"
              data-testid={`approval-reject-${l.id}`}
            >
              REJECT
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}
